import { useState, useEffect } from 'react';
import { CortexDialog, CortexDialogBody, CortexDialogFooter } from '../dialogs/CortexDialog';
import { Plus as AddIcon, Trash2 as DeleteIcon, Pencil as EditIcon } from 'lucide-react';
import { Button, Chip, Input, Textarea, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { assistantsApiService } from '../../services/assistantsApiService';
import DataTable, { type DataTableColumn } from '../data-display/DataTable';
import type { AssistantObject, CreateAssistantRequest } from '../../types/assistantsApi';

interface Props {
  agentSlug: string;
}

export const AssistantsList = ({ agentSlug }: Props) => {
  const [assistants, setAssistants] = useState<AssistantObject[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CreateAssistantRequest>({ model: 'gpt-4o', name: '', instructions: '' });

  useEffect(() => {
    loadAssistants();
  }, [agentSlug]);

  const loadAssistants = async () => {
    setLoading(true);
    try {
      const result = await assistantsApiService.listAssistants(agentSlug);
      setAssistants(result.data ?? []);
    } catch {
      setAssistants([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await assistantsApiService.modifyAssistant(agentSlug, editingId, form);
      } else {
        await assistantsApiService.createAssistant(agentSlug, form);
      }
      setDialogOpen(false);
      setEditingId(null);
      setForm({ model: 'gpt-4o', name: '', instructions: '' });
      loadAssistants();
    } catch (err) {
      console.error('Failed to save assistant', err);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await assistantsApiService.deleteAssistant(agentSlug, id);
      loadAssistants();
    } catch (err) {
      console.error('Failed to delete assistant', err);
    }
  };

  const openEdit = (a: AssistantObject) => {
    setEditingId(a.id);
    setForm({ model: a.model, name: a.name ?? '', instructions: a.instructions ?? '' });
    setDialogOpen(true);
  };

  const openCreate = () => {
    setEditingId(null);
    setForm({ model: 'gpt-4o', name: '', instructions: '' });
    setDialogOpen(true);
  };

  const columns: DataTableColumn<AssistantObject>[] = [
    { id: 'id', label: 'ID', render: (r) => <span className="font-mono text-xs">{r.id}</span> },
    { id: 'name', label: 'Name', render: (r) => r.name ?? '—' },
    { id: 'model', label: 'Model', render: (r) => <Chip label={r.model} variant="outlined" /> },
    {
      id: 'tools', label: 'Tools',
      render: (r) => r.tools?.length ?? 0,
    },
    {
      id: 'created_at', label: 'Created',
      render: (r) => new Date(r.created_at * 1000).toLocaleString(),
    },
    {
      id: 'actions', label: '', align: 'right',
      render: (r) => (
        <div className="flex gap-1">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" onClick={() => openEdit(r)}><EditIcon size={16} /></Button>
              </TooltipTrigger>
              <TooltipContent>Edit</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button type="button" variant="ghost" size="icon" className="text-red-500" onClick={() => handleDelete(r.id)}><DeleteIcon size={16} /></Button>
              </TooltipTrigger>
              <TooltipContent>Delete</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <p className="text-sm text-[var(--mui-palette-text-secondary)]">
          Manage assistants for this agent.
        </p>
        <Button size="sm" onClick={openCreate}>
          <AddIcon className="mr-1.5" size={16} />
          New Assistant
        </Button>
      </div>

      <DataTable columns={columns} rows={assistants} loading={loading} emptyMessage="No assistants created yet." />

      <CortexDialog open={dialogOpen} onClose={() => setDialogOpen(false)} title={editingId ? 'Edit Assistant' : 'Create Assistant'} size="lg" data-testid="assistant-dialog">
        <CortexDialogBody>
          <div className="flex flex-col gap-4 mt-2">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <Input
                type="text"
                value={form.name ?? ''}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Model</label>
              <Input
                type="text"
                value={form.model}
                onChange={(e) => setForm({ ...form, model: e.target.value })}
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Instructions</label>
              <Textarea
                value={form.instructions ?? ''}
                onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                rows={4}
                className="w-full"
              />
            </div>
          </div>
        </CortexDialogBody>
        <CortexDialogFooter>
          <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave}>{editingId ? 'Save' : 'Create'}</Button>
        </CortexDialogFooter>
      </CortexDialog>
    </div>
  );
};
